import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useSettingsPage, useUpdateSettingsPage } from '../hooks/useCmsPages';
import Swal from 'sweetalert2';

const Settings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home_mobile_section');
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Fetch settings data using React Query
  const {
    data: settingsData,
    isLoading,
    error,
  } = useSettingsPage();

  // Update settings mutation
  const updateSettingsMutation = useUpdateSettingsPage();

  // Initialize edited data when settings data is loaded
  React.useEffect(() => {
    if (settingsData?.data && !isEditing) {
      setEditedData(settingsData.data);
    }
  }, [settingsData, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({ ...settingsData.data });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({ ...settingsData.data });
  };

  const handleSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync(editedData);
      setIsEditing(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Settings updated successfully!',
        timer: 2000,
        showConfirmButton: false,
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#f59e0b] text-white font-bold py-2 px-4 rounded',
        }
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update settings',
        confirmButtonText: 'OK',
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        }
      });
    }
  };

  const handleInputChange = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTextChange = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: {
          ...prev[section][field],
          en: value
        }
      }
    }));
  };

  const tabs = [
    { id: 'home_mobile_section', label: 'Home Mobile Section', icon: 'pi pi-mobile' },
    { id: 'home_about_section', label: 'Home About Section', icon: 'pi pi-info-circle' },
    { id: 'footer_links', label: 'Footer Links', icon: 'pi pi-link' },
    { id: 'contact_us', label: 'Contact Us', icon: 'pi pi-phone' },
  ];

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="Settings"
            description="Manage application settings"
            user={user}
          />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  const renderHomeMobileSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text Content
        </label>
        {isEditing ? (
          <div className="border border-gray-300 rounded-md">
            <Editor
              value={editedData.home_mobile_section?.text?.en || ''}
              onTextChange={(e) => handleTextChange('home_mobile_section', 'text', e.htmlValue)}
              style={{ height: '200px' }}
            />
          </div>
        ) : (
          <div 
            className="prose max-w-none p-4 border border-gray-200 rounded-md bg-gray-50"
            dangerouslySetInnerHTML={{ __html: settingsData?.data?.home_mobile_section?.text?.en || 'No content set' }}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            iOS App Link
          </label>
          {isEditing ? (
            <input
              type="url"
              value={editedData.home_mobile_section?.iosLink || ''}
              onChange={(e) => handleInputChange('home_mobile_section', 'iosLink', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter iOS app link"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.home_mobile_section?.iosLink || 'No link set'}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Android App Link
          </label>
          {isEditing ? (
            <input
              type="url"
              value={editedData.home_mobile_section?.androidLink || ''}
              onChange={(e) => handleInputChange('home_mobile_section', 'androidLink', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter Android app link"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.home_mobile_section?.androidLink || 'No link set'}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderHomeAboutSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        {isEditing ? (
          <input
            type="text"
            value={editedData.home_about_section?.title?.en || ''}
            onChange={(e) => handleTextChange('home_about_section', 'title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter title"
          />
        ) : (
          <div className="text-gray-900 font-medium p-2 bg-gray-50 rounded-md">
            {settingsData?.data?.home_about_section?.title?.en || 'No title set'}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text Content
        </label>
        {isEditing ? (
          <div className="border border-gray-300 rounded-md">
            <Editor
              value={editedData.home_about_section?.text?.en || ''}
              onTextChange={(e) => handleTextChange('home_about_section', 'text', e.htmlValue)}
              style={{ height: '200px' }}
            />
          </div>
        ) : (
          <div 
            className="prose max-w-none p-4 border border-gray-200 rounded-md bg-gray-50"
            dangerouslySetInnerHTML={{ __html: settingsData?.data?.home_about_section?.text?.en || 'No content set' }}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Companies Number
          </label>
          {isEditing ? (
            <input
              type="number"
              value={editedData.home_about_section?.companiesNumber || ''}
              onChange={(e) => handleInputChange('home_about_section', 'companiesNumber', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter companies number"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.home_about_section?.companiesNumber || 0}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Customers Number
          </label>
          {isEditing ? (
            <input
              type="number"
              value={editedData.home_about_section?.newCustomersNumber || ''}
              onChange={(e) => handleInputChange('home_about_section', 'newCustomersNumber', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter new customers number"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.home_about_section?.newCustomersNumber || 0}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderFooterLinks = () => (
    <div className="space-y-4">
      {['facebook', 'instagram', 'twitter', 'linkedin', 'youtube'].map((platform) => (
        <div key={platform}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {platform} Link
          </label>
          {isEditing ? (
            <input
              type="url"
              value={editedData.footer_links?.[platform] || ''}
              onChange={(e) => handleInputChange('footer_links', platform, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={`Enter ${platform} link`}
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.footer_links?.[platform] || 'No link set'}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderContactUs = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Brief Description
        </label>
        {isEditing ? (
          <div className="border border-gray-300 rounded-md">
            <Editor
              value={editedData.contact_us?.brief?.en || ''}
              onTextChange={(e) => handleTextChange('contact_us', 'brief', e.htmlValue)}
              style={{ height: '200px' }}
            />
          </div>
        ) : (
          <div 
            className="prose max-w-none p-4 border border-gray-200 rounded-md bg-gray-50"
            dangerouslySetInnerHTML={{ __html: settingsData?.data?.contact_us?.brief?.en || 'No content set' }}
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address
        </label>
        {isEditing ? (
          <textarea
            value={editedData.contact_us?.address || ''}
            onChange={(e) => handleInputChange('contact_us', 'address', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows="3"
            placeholder="Enter address"
          />
        ) : (
          <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
            {settingsData?.data?.contact_us?.address || 'No address set'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          {isEditing ? (
            <input
              type="email"
              value={editedData.contact_us?.email || ''}
              onChange={(e) => handleInputChange('contact_us', 'email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.contact_us?.email || 'No email set'}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Numbers
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editedData.contact_us?.phoneNumbers || ''}
              onChange={(e) => handleInputChange('contact_us', 'phoneNumbers', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter phone numbers (comma separated)"
            />
          ) : (
            <div className="text-gray-900 p-2 bg-gray-50 rounded-md">
              {settingsData?.data?.contact_us?.phoneNumbers || 'No phone numbers set'}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home_mobile_section':
        return renderHomeMobileSection();
      case 'home_about_section':
        return renderHomeAboutSection();
      case 'footer_links':
        return renderFooterLinks();
      case 'contact_us':
        return renderContactUs();
      default:
        return null;
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Settings"
          description="Manage application settings"
        />

        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Settings Management</h2>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md transition duration-200"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                      disabled={updateSettingsMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateSettingsMutation.isPending}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200 disabled:opacity-50"
                    >
                      {updateSettingsMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex">
            {/* Sidebar */}
            <div className="w-64 bg-gray-50 border-r border-gray-200">
              <nav className="p-4 space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <i className={`${tab.icon} mr-3`}></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {tabs.find(tab => tab.id === activeTab)?.label}
                </h3>
              </div>
              
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings; 